import { useRef, useState } from "react";
import { Button } from "@trillion/ui/components/ui/button";
import { Input } from "@trillion/ui/components/ui/input";
import { Textarea } from "@trillion/ui/components/ui/textarea";

interface InlineEditTextProps {
  /** Persisted value; "" when none. */
  value: string;
  /** Accessible name, e.g. "Team name" / "Team description". */
  label: string;
  /** false renders plain text, and nothing at all when value is "". */
  editable: boolean;
  /** Resolves true to leave edit mode, false to stay editing. */
  onSave: (next: string) => Promise<boolean>;
  multiline?: boolean;
  /** A blank-after-trim value is refused inline and onSave is not called. */
  required?: boolean;
  maxLength?: number;
  /** Display text when value is "" and editable. */
  placeholder?: string;
  /** Classes for the display text. */
  className?: string;
}

/**
 * Show or edit one text value and hand back a committed string. Makes no API calls.
 * Enter commits a single-line field; Escape or Cancel restores the value; blur does not commit.
 */
export function InlineEditText({
  value,
  label,
  editable,
  onSave,
  multiline = false,
  required = false,
  maxLength,
  placeholder,
  className,
}: InlineEditTextProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const inFlight = useRef(false);

  if (!editable) {
    return value ? <span className={className}>{value}</span> : null;
  }

  function startEditing() {
    setDraft(value);
    setError(null);
    setEditing(true);
  }

  function cancel() {
    setEditing(false);
    setError(null);
  }

  async function commit() {
    if (inFlight.current) return;
    const next = draft.trim();
    if (required && next === "") {
      setError(`${label} cannot be blank`);
      return;
    }
    if (next === value) {
      cancel();
      return;
    }
    inFlight.current = true;
    setSaving(true);
    try {
      if (await onSave(next)) {
        setEditing(false);
        setError(null);
      }
    } finally {
      inFlight.current = false;
      setSaving(false);
    }
  }

  if (!editing) {
    return (
      <button
        type="button"
        aria-label={label}
        onClick={startEditing}
        className={`cursor-text rounded text-left hover:bg-muted/50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
          value ? "" : "text-muted-foreground"
        } ${className ?? ""}`}
      >
        {value || placeholder || label}
      </button>
    );
  }

  const fieldProps = {
    "aria-label": label,
    value: draft,
    maxLength,
    autoFocus: true,
    disabled: saving,
    onChange: (e: { target: { value: string } }) => {
      setDraft(e.target.value);
      if (error) setError(null);
    },
  };

  return (
    <span className="flex w-full flex-col gap-2">
      {multiline ? (
        <Textarea
          {...fieldProps}
          onKeyDown={(e) => {
            // Enter/Escape inside an IME composition accept or dismiss a candidate, not the edit.
            if (e.nativeEvent.isComposing) return;
            if (e.key === "Escape") cancel();
          }}
        />
      ) : (
        <Input
          {...fieldProps}
          onKeyDown={(e) => {
            if (e.nativeEvent.isComposing) return;
            if (e.key === "Enter") {
              e.preventDefault();
              void commit();
            } else if (e.key === "Escape") {
              cancel();
            }
          }}
        />
      )}
      {error && (
        <span role="alert" className="text-sm text-destructive">
          {error}
        </span>
      )}
      <span className="flex gap-2">
        <Button type="button" size="sm" disabled={saving} onClick={() => void commit()}>
          Save
        </Button>
        <Button type="button" size="sm" variant="ghost" disabled={saving} onClick={cancel}>
          Cancel
        </Button>
      </span>
    </span>
  );
}
